import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/content";
import { categories, movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useFavorites } from "@/hooks/useFavorites";
import ShareDialog from "@/pages/detail/components/ShareDialog";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function MovieDetail({ movie }: { movie: Movie }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const favorite = isFavorite(movie.id);
  const category = categories.find((c) => c.key === movie.type);

  usePageMeta(
    `${movie.title} - 蜜月TV`,
    `${movie.title}：${movie.year}年${movie.region}${category?.title ?? "影视"}，类型${movie.genres.join("、")}，导演${movie.director}，主演${movie.cast.join("、")}。${movie.synopsis.slice(0, 40)}…`,
  );

  const related = useMemo(
    () => movies.filter((m) => m.type === movie.type && m.id !== movie.id).sort((a, b) => b.rating - a.rating).slice(0, 4),
    [movie.id, movie.type],
  );

  const infoRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join("、") },
    { label: "地区", value: movie.region },
    { label: "年份", value: String(movie.year) },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: category?.title ?? "分类", to: `/category/${movie.type}` },
          { label: movie.title },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 mt-5">
        <div className="w-full">
          <div className="w-full max-w-[300px] mx-auto lg:mx-0 aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
            <img
              src={movie.cover}
              alt={`${movie.title} 主图封面`}
              title={`${movie.title} - 蜜月TV影视资料`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                favorite
                  ? "bg-accent-100 text-accent-800 border border-accent-300"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
              {favorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              aria-label="分享"
              className="w-11 h-11 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-share-forward-line text-lg" />
            </button>
          </div>
          {favorite && (
            <p className="mt-2 text-xs text-accent-700 flex items-center gap-1">
              <i className="ri-check-line" /> 已加入我的收藏，可在本地随时查看
            </p>
          )}

          <div className="mt-5 rounded-lg bg-background-50 border border-background-200 divide-y divide-background-200">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs text-foreground-500">评分</span>
              <span className="text-lg font-black text-primary-600">{movie.rating.toFixed(1)}</span>
            </div>
            {infoRows.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 px-4 py-3">
                <span className="text-xs text-foreground-500 shrink-0">{row.label}</span>
                <span className="text-sm text-foreground-800 text-right">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Link
              to={`/category/${movie.type}`}
              className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors"
            >
              {category?.title ?? "影视"}
            </Link>
            {movie.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-4xl font-black text-foreground-950">{movie.title}</h1>
          <p className="text-sm text-foreground-500 mt-2">
            {movie.year} · {movie.region} · {movie.genres.join(" / ")} · 评分 {movie.rating.toFixed(1)}
          </p>

          <section className="mt-7">
            <h2 className="text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500" />
              剧情简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-7">{movie.synopsis}</p>
            <p className="text-sm md:text-base text-foreground-700 leading-7 mt-4">
              本剧由{movie.director}执导，{movie.cast.join("、")}联袂出演。作品以细腻的人物刻画与扎实的叙事节奏，
              呈现出兼具观赏性与话题度的质感表达，是近期值得关注的影视作品之一。
            </p>
          </section>

          <section className="mt-10">
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <Link to={`/category/${movie.type}`} className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
                更多{movie.type === "tv" ? "电视剧" : movie.type === "movie" ? "电影" : "动漫"} <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>

          <div className="mt-8 rounded-lg bg-secondary-50 border border-secondary-100 p-4 text-xs text-foreground-500 flex items-start gap-2">
            <i className="ri-information-line text-secondary-500 mt-0.5" />
            <span>本站内容均为演示数据，暂不提供在线播放与下载功能。</span>
          </div>
        </div>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={movie.title} />
    </div>
  );
}